import axios, {
  TypedAxiosInstance,
  TypedAxiosRequestConfig,
} from "restyped-axios";

import { showToast } from "./Layout/ToastDisplay";
import {
  API_UsersYouAreMessaging,
  API_Messages_Obj,
  ChipsAPI,
  Toast,
} from "../../src/apidefs";
import { RestypedBase } from "restyped";
import { NavToolbarInstance } from "./Toolbar/NavToolbar";

class APIWrapper<T extends RestypedBase> {
  api: TypedAxiosInstance<T>;

  private handleToast(data: Toast): void {
    showToast(data);

    if (data.goldDiff && NavToolbarInstance) {
      NavToolbarInstance.giveCoins(data.goldDiff);
    }
  }

  public isToast(data: any): data is Toast {
    return data.type === "error" || data.type === "success";
  }

  public isErrorToast<T>(data: Toast | T): boolean {
    return this.isToast(data) && data.type === "error";
  }

  public isSuccessToast<T>(data: Toast | T): boolean {
    return this.isToast(data) && data.type === "success";
  }

  constructor() {
    this.api = axios.create<T>({});
  }

  async request<Path extends keyof T, Method extends keyof T[Path] = "GET">(
    config: TypedAxiosRequestConfig<T, Path, Method>
  ): Promise<T[Path]["GET"]["response"]> {
    const { data } = await this.api.request(config);

    if (this.isToast(data)) this.handleToast(data);

    return data;
  }

  async get<Path extends keyof T>(
    url: Path | string,
    config?: TypedAxiosRequestConfig<T, Path, "GET">
  ): Promise<T[Path]["GET"]["response"]> {
    const { data } = await this.api.get(url, config);

    if (this.isToast(data)) this.handleToast(data);

    return data;
  }

  async post<Path extends keyof T>(
    url: Path | string,
    body?: T[Path]["POST"]["body"]
  ): Promise<T[Path]["POST"]["response"]> {
    const { data } = await this.api.post(url, body);

    if (this.isToast(data)) this.handleToast(data);

    return data;
  }

  async put<Path extends keyof T>(
    url: Path | string,
    body?: T[Path]["PUT"]["body"]
  ): Promise<T[Path]["PUT"]["response"]> {
    const { data } = await this.api.put(url, body);

    if (this.isToast(data)) this.handleToast(data);

    return data;
  }

  async delete<Path extends keyof T>(
    url: Path | string,
    body?: T[Path]["DELETE"]["body"]
  ): Promise<T[Path]["DELETE"]["response"]> {
    const { data } = await this.api.delete(url, body);

    if (this.isToast(data)) this.handleToast(data);

    return data;
  }

  async postFile<Path extends keyof T>(
    url: Path | string,
    body?: T[Path]["POST"]["body"]
  ): Promise<T[Path]["POST"]["response"]> {
    const formData = new FormData();

    for (const key of Object.keys(body || {})) {
      formData.append(key, body![key]);
    }

    const { data } = await this.api.post(url, formData);

    if (this.isToast(data)) this.handleToast(data);

    return data;
  }
}

export const api = new APIWrapper<ChipsAPI>();

export class API {
  public static HandleError(): boolean {
    /*
    if (isErrorResponse(err)) {
      showError(err.msg);

      return true;
    }

    if (isNewErrorResponse(err)) {
      showError(err.msg);

      return true;
    }
    */

    return false;
  }

  public static HandleToast(data: Toast): boolean {
    if (data.type === "error" || data.type === "success") {
      showToast(data);

      return false;
    }

    return true;
  }

  public static IsToast(data: any): data is Toast {
    return data.type === "error" || data.type === "success";
  }

  public static async getMessagedUsers(
    initialMessagedUser: number | undefined
  ): Promise<API_UsersYouAreMessaging> {
    if (initialMessagedUser) {
      return (await axios.get(`/api/messages/userinfo/${initialMessagedUser}`))
        .data;
    } else {
      return (await axios.get(`/api/messages/userinfo/`)).data;
    }
  }

  public static async getMessages(): Promise<API_Messages_Obj> {
    return (await axios.get(`/api/messages/messages/`)).data;
  }
}
